/**
 * respond_approval tool - Respond to a pending approval
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const respondApprovalInputSchema = z.object({
  approvalId: z.string().describe("The approval ID (GUID)"),
  response: z
    .enum(["Approve", "Reject"])
    .describe("The response: 'Approve' or 'Reject'"),
  comments: z
    .string()
    .optional()
    .describe("Optional comments for the response"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
});

export type RespondApprovalInput = z.infer<typeof respondApprovalInputSchema>;

export const respondApprovalTool = {
  name: "respond_approval",
  description:
    "Respond to a pending approval request. Use 'Approve' to approve or 'Reject' to reject the request.",
  inputSchema: {
    type: "object" as const,
    properties: {
      approvalId: {
        type: "string",
        description: "The approval ID (GUID)",
      },
      response: {
        type: "string",
        enum: ["Approve", "Reject"],
        description: "The response: 'Approve' or 'Reject'",
      },
      comments: {
        type: "string",
        description: "Optional comments for the response",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
    },
    required: ["approvalId", "response"],
  },
};

export async function handleRespondApproval(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  const parsed = respondApprovalInputSchema.parse(input);

  logger.info(
    { approvalId: parsed.approvalId, response: parsed.response },
    "Executing respond_approval"
  );

  await api.respondApproval(
    parsed.approvalId,
    parsed.response,
    parsed.comments,
    parsed.environment
  );

  const verb = parsed.response.toLowerCase() === "approve" ? "Approved" : parsed.response.toLowerCase() === "reject" ? "Rejected" : `Responded "${parsed.response}" to`;
  return `${verb} approval ${parsed.approvalId}${parsed.comments ? ` with comment: ${parsed.comments}` : ""}. The requesting flow continues from its approval step; list_approvals no longer shows this item as pending.`;
}
